/**
 * TriangulationDashboard (DC-1492)
 *
 * Landing page at /triangulation. Lists the org's domains with their
 * dataset / interview / document counts. Each row links into the per-domain
 * triangulation view at /triangulation/:domainId.
 *
 * Placement note (locked 2026-05-14): top-level route rather than nested
 * under /settings/governance/triangulation per the Sprint 15 design doc.
 * The domain is org-scoped (not journey-scoped), so a top-level entry
 * matches its scope; the cross-link from Execution Risk (DC-1494) closes the
 * "how does a user discover this" loop.
 *
 * Editorial redesign (2026-05-26, DC-1622 follow-on): ported to the cream/teal
 * "Point-of-View" system to match the per-domain view. Shares the scoped
 * `.tri-view` stylesheet. data-testid / text / link contracts unchanged.
 *
 * Data: GET /api/triangulation/domains → DomainSummary[]
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { useApiClient } from '../../hooks/useApiClient';
import type { DomainSummary } from '../../types/triangulation';
import {
  buildDomainSparklineModel,
  type DomainSparklineModel,
  type DomainSparklinePointDTO,
} from '../../components/trajectory/domainSparklineData';
import '../../styles/triangulation.css';

// DC-1658 (surface c): the sparkline carries ApexCharts — lazy so it stays out
// of the dashboard's entry chunk and only loads once a domain has a trend.
const ReadinessSparkline = React.lazy(
  () => import('../../components/trajectory/ReadinessSparkline'),
);

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; domains: DomainSummary[] }
  | { kind: 'error'; message: string };

export const TriangulationDashboard: React.FC = () => {
  // Destructure stable references — `useApiClient()` returns a new wrapper
  // object each render, which would re-fire `useCallback` deps and trip an
  // infinite render loop. `get` itself is `useCallback`-stable in the real
  // hook, and the test mock returns a stable jest.fn() reference too.
  const { get } = useApiClient();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  // DC-1658 surface (c): per-domain sparkline models, keyed by domain id. Loaded
  // best-effort alongside the domain list; a failure leaves rows without a
  // sparkline (the list still renders).
  const [sparklines, setSparklines] = useState<Map<string, DomainSparklineModel>>(new Map());

  // DC-1593: lower-probability sibling of DomainTriangulationView's race. If
  // the user retries while a prior load() is still in flight (or Strict Mode
  // double-fires), the older fetch's setState shouldn't be allowed to land
  // on top of the newer one. A monotonic request id makes the bail-out
  // cheap and obvious.
  const latestRequestIdRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    setState({ kind: 'loading' });
    try {
      // Domains + sparklines in parallel (no async waterfall, UX spec §7). The
      // sparkline fetch is best-effort: its failure must not blank the list.
      const [domains, sparkRaw] = await Promise.all([
        get<DomainSummary[]>('/api/triangulation/domains', { skipCache: true }),
        get<Record<string, DomainSparklinePointDTO[]>>('/api/triangulation/domains/sparklines', {
          skipCache: true,
        }).catch(() => ({}) as Record<string, DomainSparklinePointDTO[]>),
      ]);
      if (requestId !== latestRequestIdRef.current) return; // stale
      const models = new Map<string, DomainSparklineModel>();
      for (const [domainId, points] of Object.entries(sparkRaw)) {
        models.set(domainId, buildDomainSparklineModel(points));
      }
      setSparklines(models);
      setState({ kind: 'ready', domains });
    } catch (err) {
      if (requestId !== latestRequestIdRef.current) return; // stale
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to load domains',
      });
    }
  }, [get]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="tri-view">
      <div className="tri-inner">
        <header>
          <div className="tri-eyebrow">Per-domain · three independent signals</div>
          <h1 className="tri-domain">Triangulated FAIR Assessment</h1>
          <p className="tri-domain-desc">
            A reading per data domain across the three independent FAIR signals — Technical
            scoring, Perceived sentiment, and Governed evidence.
          </p>
        </header>

        {state.kind === 'loading' && (
          <div className="tri-status" role="status" aria-live="polite">
            Loading domains…
          </div>
        )}

        {state.kind === 'error' && (
          <div className="tri-alert" role="alert">
            <AlertCircle size={20} aria-hidden="true" />
            <div>
              <p className="tri-alert-title">Couldn’t load domains</p>
              <p className="tri-alert-detail">{state.message}</p>
              <button type="button" onClick={load} className="tri-retry">
                Retry
              </button>
            </div>
          </div>
        )}

        {state.kind === 'ready' && state.domains.length === 0 && (
          <div className="tri-empty">
            <p className="tri-empty-title">No domains yet.</p>
            <p className="tri-empty-copy">
              Create a domain to start triangulating signal across your datasets, interviews, and
              governance documents.
            </p>
          </div>
        )}

        {state.kind === 'ready' && state.domains.length > 0 && (
          <ul className="tri-domain-list" data-testid="domain-list">
            {state.domains.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/triangulation/${d.id}`}
                  data-testid={`domain-row-${d.slug}`}
                  className="tri-domain-row"
                >
                  <div className="tri-domain-row-main">
                    <h2 className="tri-domain-row-name">{d.name}</h2>
                    {d.description && <p className="tri-domain-row-desc">{d.description}</p>}
                  </div>

                  <div className="tri-domain-counts">
                    <Count label="Datasets" value={d.dataset_count} />
                    <Count label="Interviews" value={d.interview_count} />
                    <Count label="Documents" value={d.document_count} />
                  </div>

                  <SparkStrip model={sparklines.get(d.id)} name={d.name} />

                  <ArrowRight size={18} aria-hidden="true" className="tri-domain-arrow" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

const Count: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <span className="tri-count" title={label}>
    <span className="tri-count-num">{value}</span>
    <span className="tri-count-lbl">{label}</span>
  </span>
);

// ============================================================================
// DC-1658 surface (c) — per-domain readiness sparkline strip
// ============================================================================

function fmtDelta(d: number): string {
  return d > 0 ? `+${d}` : String(d);
}

/**
 * Current composite + since-baseline delta (the sparkline's text equivalent,
 * which lives in the row so a non-visual reader gets the trend without the
 * chart) + the trend line + low-coverage / rubric-change badges. Renders
 * nothing until a domain has at least one reading.
 */
const SparkStrip: React.FC<{ model: DomainSparklineModel | undefined; name: string }> = ({
  model,
  name,
}) => {
  if (!model || model.latest === null) return null;

  const delta = model.delta;
  const deltaClass =
    delta === null || delta === 0
      ? 'tri-spark-delta tri-spark-delta--neutral'
      : delta > 0
        ? 'tri-spark-delta tri-spark-delta--up'
        : 'tri-spark-delta tri-spark-delta--down';
  const arrow = delta === null || delta === 0 ? '' : delta > 0 ? '▲' : '▼';

  return (
    <div className="tri-spark">
      <div className="tri-spark-read">
        <span className="tri-spark-value">
          {model.latest}
          <span className="tri-spark-value-unit">/100</span>
        </span>
        {delta !== null ? (
          <span className={deltaClass}>
            {arrow} {fmtDelta(delta)} since baseline
          </span>
        ) : (
          <span className="tri-spark-delta tri-spark-delta--neutral">baseline</span>
        )}
      </div>

      {model.hasTrend && (
        <React.Suspense fallback={<span className="dsp" aria-hidden="true" />}>
          <ReadinessSparkline model={model} label={name} />
        </React.Suspense>
      )}

      {(model.lowCoverage || model.rubricChanged) && (
        <div className="tri-spark-badges">
          {model.lowCoverage && <span className="tri-spark-badge">Low coverage</span>}
          {model.rubricChanged && (
            <span className="tri-spark-badge" title="Scoring rubric changed in this window">
              Rubric v↑
            </span>
          )}
        </div>
      )}
    </div>
  );
};
